import {FC, useContext, useState} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';

import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {Box, IconButton, InputAdornment, Modal, Stack, Typography} from '@mui/material';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import {UserAccountService} from '../../../api/services/user-account.service';

import {currentUserAccountIdState, userAccountsQueryTrigger} from '../../../store/users/atoms';
import {passwordRegExp} from '../../../shared/validation/passwordRegExp';
import {EventBusContext} from '../../../providers/EventBus';
import {authState} from '../../../store/auth/atoms';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  width: 320,
  p: 2,
};

interface IChangePassword {
  open: boolean;
  handleClose: () => void;
}

export const ChangePassword: FC<IChangePassword> = ({open, handleClose}) => {
  const currentUserId = useRecoilValue(currentUserAccountIdState);
  const triggerUsersQuery = useSetRecoilState(userAccountsQueryTrigger);
  const {
    control,
    getValues,
    trigger,
    reset,
    watch,
    formState: {errors},
  } = useForm({mode: 'onChange'});
  const [loading, setLoading] = useState(false);
  const {bus} = useContext(EventBusContext);
  const {user} = useRecoilValue(authState);
  const [showPassword, setShowPassword] = useState(false);

  const password = watch('password');
  const checkedPassword = watch('checkedPassword');

  const isAdmin = checkAuthorities(
    [TypeAccountRoles.ADMIN, TypeAccountRoles.FUNCTIONAL_ADMIN],
    [...(user?.roles || [])],
  );

  const onChangePassword = async () => {
    const data = getValues();

    setLoading(true);
    try {
      const payload = {
        uid: currentUserId as string,
        newPassword: data.password,
        isTemporary: false,
      };

      await (isAdmin ? UserAccountService.changePasswordAdmin : UserAccountService.changePasswordManager)(payload);

      toast.success('Пароль изменен', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setTimeout(() => {
        setLoading(false);
        triggerUsersQuery((v) => v + 1);
        handleClose();
        reset();
      }, 1000);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Box sx={style}>
        <Stack direction="column" spacing={1.5}>
          <Stack direction="row" sx={{justifyContent: 'center'}}>
            <Typography variant="h6">Изменение пароля</Typography>
          </Stack>
          <Stack direction="column" spacing={0.25}>
            <Typography variant="subtitle2">Введите новый пароль</Typography>
            <ControlledInput
              name="password"
              control={control}
              size="small"
              rules={{
                required: true,
                pattern: {
                  value: passwordRegExp,
                  message:
                    'От 12 символов с использованием цифр, спец. символов, латиницы, наличием строчных и прописных символов',
                },
              }}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack>
            <Typography variant="subtitle2">Подтвердите пароль</Typography>
            <ControlledInput
              name="checkedPassword"
              type="password"
              control={control}
              size="small"
              rules={{
                required: true,
                validate: {
                  isTheSame: (v, values) => {
                    return values.password === v ? true : 'Пароли не совпадают';
                  },
                },
              }}
            />
          </Stack>
          <Stack direction="column"></Stack>
          <Stack direction="row" sx={{width: '100%'}}>
            <LoadingButton
              sx={{width: '100%'}}
              variant="contained"
              onClick={() => {
                trigger().then((bool) => {
                  bool && onChangePassword();
                });
              }}
              loading={loading}
              disabled={!password || !checkedPassword || !!errors.password || !!errors.checkedPassword}
            >
              Изменить
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
